export type TitleImportRunStatus =
  | "Queued"
  | "Running"
  | "Completed"
  | "Failed"
  | "Cancelled";

export type TitleImportRunSource =
  | "Scheduled"
  | "Manual"
  /** Reports what the audit would remove, without removing it. */
  | "Audit"
  /** Removes what the last audit report recorded. */
  | "AuditApply";

export type TitleAuditFindingReason =
  | "BlockedTheme"
  | "BlockedKeyword"
  | "UnwantedPlatforms"
  | "MissingFromIgdb";

/** A title the audit judged should be removed, and why. */
export interface TitleAuditFinding {
  id: string;
  runId: string;
  titleId: string;
  titleName: string;
  igdbId?: string;
  reason: TitleAuditFindingReason;
  detail?: string;
  removedAt?: string;
}

/** One execution of the IGDB title import. */
export interface TitleImportRun {
  id: string;
  source: TitleImportRunSource;
  status: TitleImportRunStatus;
  fromDate: string;
  toDate: string;
  requestedByUserId?: string;
  queuedAt: string;
  startedAt?: string;
  completedAt?: string;
  cancelRequested: boolean;
  progressStage?: string;
  progressCurrent: number;
  progressTotal: number;
  gamesDiscovered: number;
  titlesCreated: number;
  titlesUpdated: number;
  /** Examined and left alone because nothing about them had changed. */
  titlesUnchanged: number;
  titlesSkippedContent: number;
  titlesSkippedStatus: number;
  titlesFailed: number;
  /** Audit only: how many titles were recorded as needing removal. */
  titlesFlagged: number;
  /** Audit only: how many titles were actually removed. */
  titlesRemoved: number;
  /** Audit only: the last IGDB id reached, or absent once the catalogue is finished. */
  auditCursor?: string;
  errorMessage?: string;
}

/** The findings waiting to be acted on, with how many there are in total. */
export interface TitleAuditFindings {
  /** Capped, so this can be shorter than totalOutstanding. */
  findings: TitleAuditFinding[];
  totalOutstanding: number;
}

export interface TitleImportStatus {
  /** The run that is queued or running. While this is set no further import can start. */
  activeRun?: TitleImportRun;
  recentRuns: TitleImportRun[];
}

export interface StartTitleImportRequest {
  fromDate: string;
  toDate: string;
}

/** A run holds the single import slot while it is queued or running. */
export const isTitleImportRunActive = (run: TitleImportRun): boolean =>
  run.status === "Queued" || run.status === "Running";
